import { useEffect, useRef } from 'react'
import { Barcode } from 'shared/components/barcode'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { formatDateTime } from 'shared/lib/utils/date'

/**
 * A Bag's label (spec §26, user story 62; ADR-0025; ticket 33): the Bag's code as
 * a barcode and in print, its Point and the date it was closed — no seal, no
 * weight (ADR-0047). It is printed once the Bag is closed — the label is only true of
 * a closed Bag — and reprinted from the hub's list of closed Bags.
 *
 * Printing is the browser's (`window.print`); the page's print style sends only
 * the element marked `data-print-label` to paper. `print` is injected so tests see
 * the call.
 */

/** What the label says — all of it read from the closed Bag. */
export interface BagLabelData {
	code: string
	pointCode: string
	sealedAt: Date | string | null
}

const printViaBrowser = () => window.print()

/** The label itself, as it goes to paper. Black on white in either theme. */
export function BagLabel({ bag }: { bag: BagLabelData }) {
	const { t } = useTranslate()
	return (
		<div
			data-print-label
			data-testid='bag-label'
			className='flex w-[360px] flex-col gap-1.5 border-[3px] border-black bg-white p-4 font-mono text-black'
		>
			<Barcode value={bag.code} className='h-16 w-full' />
			<div className='text-3xl font-extrabold' data-testid='bag-label-code'>
				{bag.code}
			</div>
			<div className='text-xl font-semibold' data-testid='bag-label-point'>
				→ {bag.pointCode}
			</div>
			<div data-testid='bag-label-date'>{t('bagLabel.date', { date: formatDateTime(bag.sealedAt) })}</div>
		</div>
	)
}

/**
 * The label on screen with «Печать этикетки». With `printNow` it goes to the
 * printer as soon as it is drawn — once, however often React re-runs effects.
 */
export function BagLabelPrint({
	bag,
	printNow = false,
	print = printViaBrowser,
}: {
	bag: BagLabelData
	printNow?: boolean
	/** Test seam: the browser's print. */
	print?: () => void
}) {
	const { t } = useTranslate()
	const printed = useRef(false)

	useEffect(() => {
		if (!printNow || printed.current) return
		printed.current = true
		// Let the label paint before the print dialog takes the page.
		const timer = setTimeout(print, 50)
		return () => {
			clearTimeout(timer)
			printed.current = false
		}
	}, [printNow, print])

	return (
		<div className='flex flex-col gap-2'>
			<BagLabel bag={bag} />
			<div>
				<Button type='button' variant='outline' data-testid='bag-label-print' onClick={print}>
					{t('bagLabel.print')}
				</Button>
			</div>
		</div>
	)
}
